import { useMemo } from 'react';
import Utils from '../../../../../utils/immersive_utils';

interface ScatterPlotChartConfig {
  scatterData: number[][];
  chartSeries: any[];
  xAxisConfig: any;
  yAxisConfig: any;
  tooltipConfig: any;
}

interface UseScatterPlotChartConfigProps {
  data: { x: number[]; y: number[] } | null;
  xAxis: string;
  yAxis: string;
  currency: string;
}

const formatAxisName = (axisValue: string, currency: string): string => {
  if (axisValue === 'spot') {
    return `${currency} Spot`;
  }

  const parts = axisValue.split('_');

  if (parts.length >= 2) {
    const tenor = parts[parts.length - 1];

    if (parts[0] === 'ATMF' && parts.length === 2) {
      return `${currency} ${tenor} ATMF`;
    }

    if (parts.length >= 3) {
      const strike = parts[0];
      return `${currency} ${tenor} ${strike}`;
    }

    return `${currency} ${tenor} ${parts[0]}`;
  }

  return `${currency} ${axisValue}`;
};

export const useScatterPlotChartConfig = ({
  data,
  xAxis,
  yAxis,
  currency,
}: UseScatterPlotChartConfigProps): ScatterPlotChartConfig => {
  const scatterData = useMemo(
    () =>
      !data?.x || !data?.y
        ? []
        : data.x.map((xVal: number, index: number) => [xVal, data.y[index]]),
    [data?.x, data?.y],
  );

  const axisLimits = useMemo(() => {
    if (!data?.x || !data?.y) {
      return { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
    }
    const xMinValue = Math.min(...data.x);
    const xMaxValue = Math.max(...data.x);
    const yMinValue = Math.min(...data.y);
    const yMaxValue = Math.max(...data.y);
    const xRange = xMaxValue - xMinValue || Math.abs(xMaxValue) * 0.01 || 1;
    const yRange = yMaxValue - yMinValue || Math.abs(yMaxValue) * 0.01 || 1;
    const padding = 0.02;
    const xMin = xMinValue - xRange * padding;
    const xMax = xMaxValue + xRange * padding;
    const yMin = yMinValue - yRange * padding;
    const yMax = yMaxValue + yRange * padding;

    return { xMin, xMax, yMin, yMax };
  }, [data?.x, data?.y]);

  const chartSeries = useMemo(
    () => [
      {
        type: 'scatter',
        data: scatterData,
        symbolSize: 8,
        color: '#26D9D9',
      },
    ],
    [scatterData],
  );

  const xAxisConfig = useMemo(
    () => ({
      type: 'value',
      min: axisLimits.xMin,
      max: axisLimits.xMax,
      axisLabel: {
        formatter: (value: any) =>
          xAxis !== 'spot' ? `${(value * 100).toFixed(0)}%` : Utils.valueToCurrency(value),
        showMinLabel: false,
        showMaxLabel: false,
      },
      name: formatAxisName(xAxis, currency).toUpperCase(),
      nameLocation: 'center',
      nameGap: 28,
    }),
    [axisLimits.xMin, axisLimits.xMax, xAxis, currency],
  );

  const yAxisConfig = useMemo(
    () => ({
      type: 'value',
      min: axisLimits.yMin,
      max: axisLimits.yMax,
      axisLabel: {
        formatter: (value: any) =>
          yAxis !== 'spot' ? `${(value * 100).toFixed(0)}%` : Utils.valueToCurrency(value),
        showMinLabel: false,
        showMaxLabel: false,
      },
      name: formatAxisName(yAxis, currency).toUpperCase(),
      nameLocation: 'center',
      nameGap: 40,
    }),
    [axisLimits.yMin, axisLimits.yMax, yAxis, currency],
  );

  const tooltipConfig = useMemo(
    () => ({
      trigger: 'item' as const,
      formatter: (params: any) => {
        const [xValue, yValue] = params.data;
        const xFormatted =
          xAxis !== 'spot' ? `${(xValue * 100).toFixed(2)}%` : Utils.valueToCurrency(xValue);
        const yFormatted =
          yAxis !== 'spot' ? `${(yValue * 100).toFixed(2)}%` : Utils.valueToCurrency(yValue);

        return `X: ${xFormatted}<br/>Y: ${yFormatted}`;
      },
    }),
    [xAxis, yAxis],
  );

  return {
    scatterData,
    chartSeries,
    xAxisConfig,
    yAxisConfig,
    tooltipConfig,
  };
};
